
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { SearchIcon, Info } from 'lucide-react';
import { AdminAction } from '@/types/adminTypes';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';

interface ActivityLogProps {
  adminActions: AdminAction[];
  activeAdmins?: string[];
  isLoading?: boolean;
  isThrottled?: boolean;
}

const ActivityLog = ({ adminActions, activeAdmins = [], isLoading = false, isThrottled = false }: ActivityLogProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter out invalid actions and filter by search query
  const filteredActions = adminActions
    .filter(action => action && action.performedBy) // Filter out invalid actions
    .filter(action => 
      action.performedBy.toLowerCase().includes(searchQuery.toLowerCase()) ||
      action.actionType.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (action.details && action.details.toLowerCase().includes(searchQuery.toLowerCase()))
    );

  const getActionTypeText = (actionType: string) => {
    switch (actionType) {
      case 'login': return 'Đăng nhập';
      case 'logout': return 'Đăng xuất';
      case 'reset': return 'Reset';
      default: return actionType;
    }
  };

  const getActionTypeClass = (actionType: string) => {
    switch (actionType) {
      case 'login': return 'text-green-600';
      case 'logout': return 'text-orange-600';
      case 'reset': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Nhật ký hoạt động</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex justify-center items-center h-48 text-muted-foreground">
            Đang tải dữ liệu...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Nhật ký hoạt động</CardTitle>
        <div className="flex gap-2 mt-2">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Tìm kiếm theo tên admin, loại hoạt động"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto p-6 pt-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Thời gian</TableHead>
                <TableHead>Admin</TableHead>
                <TableHead>Hoạt động</TableHead>
                <TableHead>Chi tiết</TableHead>
                <TableHead>Thiết bị</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredActions.length > 0 ? (
                filteredActions.map((action) => (
                  <TableRow key={action.actionId}>
                    <TableCell className="whitespace-nowrap">
                      {new Date(action.timestamp).toLocaleString('vi-VN')}
                    </TableCell>
                    <TableCell>
                      {action.performedBy}
                      {activeAdmins.includes(action.performedBy) && (
                        <span className="ml-2 inline-flex h-2 w-2 rounded-full bg-green-500" title="Đang hoạt động"></span>
                      )}
                    </TableCell>
                    <TableCell className={getActionTypeClass(action.actionType)}>
                      {getActionTypeText(action.actionType)}
                    </TableCell>
                    <TableCell>{action.details}</TableCell>
                    <TableCell>
                      {action.clientInfo ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1 cursor-help">
                                {action.clientInfo.device}
                                <Info size={14} className="text-muted-foreground" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <div className="space-y-1 text-xs">
                                <p><strong>Trình duyệt:</strong> {action.clientInfo.browser}</p>
                                <p><strong>Hệ điều hành:</strong> {action.clientInfo.os}</p>
                                <p><strong>Thiết bị:</strong> {action.clientInfo.device}</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-muted-foreground">N/A</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-4">
                    Không tìm thấy kết quả phù hợp
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default ActivityLog;
